import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStateScope } from '../context/StateScopeContext';
import { ArrowLeft, Sparkles, MessageSquare, Send, Phone, Video, Loader2, X, Search, PhoneIncoming, PhoneOutgoing, PhoneMissed, BarChart3, Activity } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { usePageHeader } from '../context/PageHeaderContext';
import { parseServerDate } from '../utils/dateUtils';
import DiarizedTranscript from '../components/DiarizedTranscript';

const CHARACTERS = [
  { id: 'orb',     name: 'ORB',     color: '#586D52', image: null },
  { id: 'mena',    name: 'Mena',    color: '#8B5CF6', image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/67lqvc7a_Mena%20Standard.png' },
  { id: 'katie',   name: 'Katie',   color: '#EC4899', image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/b90ayuat_katie%20standard.png' },
  { id: 'luke',    name: 'Luke',    color: '#3B82F6', image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/ue5pv5dv_Luke%20Standard.png' },
  { id: 'dorian',  name: 'Dorian',  color: '#F59E0B', image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/bi6kcptf_Dorian%20Standard.png' },
  { id: 'octavio', name: 'Octavio', color: '#10B981', image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/zi3ibx7q_Octavio%20Standard.png' },
];

const TABS = [
  { id: 'ai_chat',        label: 'AI Chat',        icon: Sparkles },
  { id: 'team_chats',     label: 'Team Chats',     icon: MessageSquare },
  { id: 'external_text',  label: 'External Texts', icon: Send },
  { id: 'team_calls',     label: 'Team Calls',     icon: Phone },
  { id: 'external_calls', label: 'External Calls', icon: Phone },
  { id: 'video_calls',    label: 'Video Calls',    icon: Video },
  { id: 'business_card_stats', label: 'Business Card Stats', icon: BarChart3 },
  { id: 'user_activity',  label: 'User Activity',  icon: Activity },
];

function fmtTs(ts) {
  if (!ts) return '';
  try {
    const d = parseServerDate(ts);
    return d ? d.toLocaleString() : '';
  } catch { return ts; }
}

// ===================== Summarize popup =====================
// Slides up from bottom to ~85vh (4/5+), covers the bottom nav, swipe-down to close.
/**
 * SummarizePopup — bottom-sheet AI summary viewer
 *
 * Workflow:
 *   ① Landing  — list of previously generated summaries (date+time
 *                cards). Big "Generate New Summary" button at the
 *                bottom triggers a fresh generation.
 *   ② Detail   — open one historical card to see the original
 *                summary + every follow-up Q&A, plus an input to
 *                ask another follow-up that gets appended.
 *
 * Backend:
 *   GET  /admin/communications/summaries?user_id=…&kind=…&[scope]
 *        → { summaries: [...] }
 *   POST /admin/communications/summarize {payload}
 *        → { text, summary_id, summary }   (creates a new history row)
 *   POST /admin/communications/summarize {…, question, summary_id}
 *        → { text, summary_id }            (appends to qa[])
 */
export function SummarizePopup({ open, onClose, api, payload, title }) {
  // 'list' (default landing) | 'detail' (an existing summary opened)
  // | 'generating' (new summary in flight, no card yet)
  const [view, setView] = useState('list');
  const [summaries, setSummaries] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [active, setActive] = useState(null); // currently-opened summary doc
  const [generating, setGenerating] = useState(false);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const sheetRef = useRef(null);
  const startYRef = useRef(null);
  const [dragY, setDragY] = useState(0);

  // Resolve scope params for GET /summaries — only include keys
  // relevant to this kind so the backend matches the same scope used
  // when the summary was originally saved.
  const scopeParams = (() => {
    const p = { user_id: payload.user_id, kind: payload.kind };
    if (payload.kind === 'ai_chat') p.character = payload.character || 'orb';
    if (payload.kind === 'team_chat') p.conversation_id = payload.conversation_id;
    if (payload.kind === 'external_text') p.phone_number = payload.phone_number;
    if (['call', 'team_call', 'external_call', 'video_call'].includes(payload.kind)) {
      if (payload.call_id) p.call_id = payload.call_id;
    }
    return p;
  })();

  // Initial load — fetch the list every time the popup opens.
  useEffect(() => {
    if (!open) {
      // Reset internal state on close so re-opening starts fresh.
      setView('list');
      setActive(null);
      setQuestion('');
      setDragY(0);
      setGenerating(false);
      return;
    }
    let alive = true;
    (async () => {
      setLoadingList(true);
      try {
        const r = await api.get('/admin/communications/summaries', { params: scopeParams });
        if (alive) {
          setSummaries(r.data.summaries || []);
        }
      } catch (e) {
        if (alive) toast.error('Could not load past summaries');
      }
      if (alive) setLoadingList(false);
    })();
    return () => { alive = false; };
  }, [open, api, JSON.stringify(payload)]);  // eslint-disable-line

  const fmtTimestamp = (iso) => {
    if (!iso) return '';
    try {
      const d = parseServerDate(iso);
      return d ? d.toLocaleString([], {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      }) : iso;
    } catch { return iso; }
  };

  const generateNew = async () => {
    setGenerating(true);
    setView('generating');
    try {
      // iter 102.5 — manual click summaries run inline; the
      // endpoint awaits the LLM and returns the finished summary.
      // (Background-only paths like recorded-call auto-summary use
      // the queue.)
      const res = await api.post('/admin/communications/summarize', { ...payload });
      const newDoc = res.data.summary || {
        id: res.data.summary_id,
        summary: res.data.text || '',
        qa: [],
        generated_at: new Date().toISOString(),
        ...payload,
      };
      setSummaries((prev) => [newDoc, ...prev]);
      setActive(newDoc);
      setView('detail');
    } catch (e) {
      toast.error('Could not generate summary');
      setView('list');
    } finally {
      setGenerating(false);
    }
  };

  const openSummary = async (s) => {
    // Pull the full doc (qa[] could be heavy on the list cache).
    try {
      const r = await api.get(`/admin/communications/summaries/${s.id}`);
      setActive(r.data);
    } catch (e) {
      // Fall back to whatever we have in the list.
      setActive(s);
    }
    setView('detail');
  };

  const ask = async () => {
    const q = question.trim();
    if (!q || !active?.id) return;
    setAsking(true);
    // Optimistic append.
    const optimistic = { question: q, answer: '...', asked_at: new Date().toISOString(), _pending: true };
    setActive((cur) => ({ ...(cur || {}), qa: [...((cur && cur.qa) || []), optimistic] }));
    setQuestion('');
    try {
      const res = await api.post('/admin/communications/summarize', {
        ...payload, question: q, summary_id: active.id,
      });
      const answer = res.data.text || '';
      setActive((cur) => {
        if (!cur) return cur;
        const qa = [...(cur.qa || [])];
        const idx = qa.findIndex((x) => x._pending && x.question === q);
        if (idx >= 0) qa[idx] = { question: q, answer, asked_at: new Date().toISOString() };
        return { ...cur, qa };
      });
    } catch (e) {
      setActive((cur) => {
        if (!cur) return cur;
        const qa = [...(cur.qa || [])];
        const idx = qa.findIndex((x) => x._pending && x.question === q);
        if (idx >= 0) qa[idx] = { question: q, answer: 'Sorry, I could not answer that.', asked_at: new Date().toISOString() };
        return { ...cur, qa };
      });
    }
    setAsking(false);
  };

  // Swipe-down to dismiss
  const onTouchStart = (e) => { startYRef.current = e.touches[0].clientY; };
  const onTouchMove = (e) => {
    if (startYRef.current == null) return;
    if (e.target && e.target.closest('[data-sheet-scroll]')) return;
    const delta = e.touches[0].clientY - startYRef.current;
    if (delta > 0) setDragY(Math.min(delta, 600));
  };
  const onTouchEnd = () => {
    if (dragY > 120) onClose();
    setDragY(0);
    startYRef.current = null;
  };

  if (!open) return null;

  // ---------- view = list (landing) ----------
  const ListView = (
    <>
      <div className="flex-1 overflow-y-auto p-4" data-sheet-scroll>
        {loadingList ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading past summaries…
          </div>
        ) : summaries.length === 0 ? (
          <div className="text-center py-10">
            <Sparkles className="w-10 h-10 mx-auto mb-2 text-[#586D52] opacity-60" />
            <p className="text-sm font-semibold">No summaries yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Tap the button below to generate your first summary.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-1">
              Previous summaries
            </p>
            {summaries.map((s) => {
              const qaCount = (s.qa || []).length;
              const preview = (s.summary || '').slice(0, 120).trim();
              return (
                <button
                  key={s.id}
                  onClick={() => openSummary(s)}
                  className="w-full text-left bg-white dark:bg-[#1a1a1a] border border-gray-100 dark:border-[#2a2a2a] hover:border-[#586D52] rounded-xl p-3 transition-colors"
                  data-testid={`summary-card-${s.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-bold text-[#586D52]">
                      {fmtTimestamp(s.generated_at)}
                    </p>
                    {qaCount > 0 && (
                      <span className="text-[10px] font-semibold text-muted-foreground bg-gray-100 dark:bg-[#252525] rounded-full px-2 py-0.5">
                        {qaCount} follow-up{qaCount === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-3 mt-1 leading-snug">
                    {preview || '(empty summary)'}
                  </p>
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div className="border-t p-3 bg-white/95 dark:bg-[#1a1a1a]/95 backdrop-blur-md">
        <Button
          className="w-full bg-[#586D52] hover:bg-[#4a5d45] text-white"
          onClick={generateNew}
          disabled={generating}
          data-testid="generate-new-summary-btn"
        >
          {generating ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4 mr-2" />
          )}
          Generate New Summary
        </Button>
      </div>
    </>
  );

  // ---------- view = generating (transient placeholder) ----------
  const GeneratingView = (
    <div className="flex-1 flex flex-col items-center justify-center p-6 gap-3">
      <Loader2 className="w-8 h-8 animate-spin text-[#586D52]" />
      <p className="text-sm font-semibold">Generating summary…</p>
      <p className="text-xs text-muted-foreground text-center max-w-xs">
        We&apos;re aggregating the latest activity. This usually takes a few seconds.
      </p>
    </div>
  );

  // ---------- view = detail (one summary opened) ----------
  const DetailView = active && (
    <>
      <div className="px-4 py-2 border-b flex items-center gap-2 bg-gray-50/80 dark:bg-[#222]">
        <button
          onClick={() => { setView('list'); setActive(null); }}
          className="text-xs font-semibold text-[#586D52] hover:underline flex items-center gap-1"
          data-testid="summary-back-to-list"
        >
          ← Back to summaries
        </button>
        <span className="ml-auto text-[10px] text-muted-foreground">
          {fmtTimestamp(active.generated_at)}
        </span>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-sheet-scroll>
        <div
          className="bg-gray-50 dark:bg-[#252525] rounded-xl p-3 text-sm whitespace-pre-wrap break-words"
          data-testid="summarize-text"
        >
          {active.summary || '(no content)'}
        </div>
        {(active.qa || []).map((entry, i) => (
          <React.Fragment key={i}>
            <div className="flex justify-end">
              <div className="max-w-[85%] rounded-2xl p-3 text-sm whitespace-pre-wrap break-words bg-[#586D52] text-white">
                {entry.question}
              </div>
            </div>
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-2xl p-3 text-sm whitespace-pre-wrap break-words bg-gray-50 dark:bg-[#252525]">
                {entry._pending ? <Loader2 className="w-3 h-3 animate-spin inline" /> : entry.answer}
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>
      <div className="border-t p-3 flex gap-2 items-end">
        <textarea
          placeholder="Ask a follow-up about this summary…"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          rows={1}
          className="flex-1 min-h-[44px] max-h-[120px] resize-none rounded-xl border bg-background px-3 py-2 text-sm"
          style={{ fontSize: '16px' }}
          data-testid="summarize-question-input"
        />
        <Button
          onClick={ask}
          disabled={asking || !question.trim()}
          className="bg-[#586D52]"
          data-testid="summarize-ask-btn"
        >
          {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </>
  );

  const sheet = (
    <div
      className="fixed inset-0 z-[9999] bg-black/60 flex items-end justify-center"
      onClick={onClose}
      data-testid="summarize-popup"
    >
      <div
        ref={sheetRef}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className="bg-white dark:bg-[#1a1a1a] rounded-t-3xl w-full max-w-xl shadow-2xl flex flex-col"
        style={{
          height: '80vh',
          transform: `translateY(${dragY}px)`,
          transition: dragY === 0 ? 'transform 200ms ease-out' : 'none',
          paddingBottom: 'max(env(safe-area-inset-bottom, 0px), var(--kb-offset, 0px))',
        }}
      >
        {/* drag handle */}
        <div className="pt-2 flex justify-center">
          <span className="w-10 h-1.5 rounded-full bg-gray-300 dark:bg-gray-700" />
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="w-4 h-4 text-[#586D52] flex-shrink-0" />
            <p className="font-bold text-sm truncate">{title || 'AI Summary'}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-[#252525] flex items-center justify-center flex-shrink-0"
            data-testid="summarize-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {view === 'list' && ListView}
        {view === 'generating' && GeneratingView}
        {view === 'detail' && DetailView}
      </div>
    </div>
  );
  return createPortal(sheet, document.body);
}

// ===================== AI Chat as a real chat interface =====================
function AiChatPage({ api, userId, user }) {
  const navigate = useNavigate();
  const [character, setCharacter] = useState('orb');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/admin/communications/${userId}/ai-chat`, { params: { character } });
        if (alive) setMessages(res.data.messages || []);
      } catch (e) { /* silent */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [api, userId, character]);

  // Build flat bubble list: each chat row → user bubble + ai bubble
  const bubbles = [];
  for (const m of messages) {
    if (m.user_message) bubbles.push({ id: `u-${m.id}`, role: 'user', text: m.user_message, ts: m.created_at });
    if (m.ai_response)   bubbles.push({ id: `a-${m.id}`, role: 'ai',   text: m.ai_response,   ts: m.created_at });
  }

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [bubbles.length]);

  const charObj = CHARACTERS.find((c) => c.id === character) || CHARACTERS[0];

  return (
    <div className="fixed inset-x-0 flex flex-col bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] kb-aware-bottom" style={{ top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))' }} data-testid="user-comm-aichat-detail">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-white/80 dark:bg-[#1a1a1a]/80 backdrop-blur-md border-b">
        <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-full hover:bg-gray-100 dark:hover:bg-[#252525] flex items-center justify-center"><ArrowLeft className="w-4 h-4" /></button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate">{user?.name || 'User'} · AI Chat</p>
          <p className="text-[11px] text-muted-foreground">with {charObj.name}</p>
        </div>
      </div>

      {/* Character filter row — even spread across full width */}
      <div className="flex-shrink-0 flex items-start justify-between gap-1 px-3 py-2 bg-white/60 dark:bg-[#1a1a1a]/60">
        {CHARACTERS.map((c) => (
          <button
            key={c.id}
            onClick={() => setCharacter(c.id)}
            className={`flex-1 min-w-0 flex flex-col items-center gap-0.5 transition-opacity ${character === c.id ? 'opacity-100' : 'opacity-50'}`}
            data-testid={`ai-filter-${c.id}`}
          >
            <div className="w-11 h-11 rounded-full overflow-hidden border-2 flex items-center justify-center" style={{ borderColor: c.color, background: c.image ? 'transparent' : c.color }}>
              {c.image ? <img src={c.image} alt={c.name} className="w-full h-full object-cover object-top" /> : <Sparkles className="w-5 h-5 text-white" />}
            </div>
            <p className="text-[10px] font-semibold truncate w-full text-center">{c.name}</p>
          </button>
        ))}
      </div>

      {/* Chat scroller */}
      <div ref={scrollerRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : bubbles.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-12">No chat history with {charObj.name}.</p>
        ) : bubbles.map((b) => (
          <div key={b.id} className={`flex ${b.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words shadow-sm ${b.role === 'user' ? 'bg-[#586D52] text-white' : 'bg-white dark:bg-[#1a1a1a] text-foreground border'}`}>
              <p>{b.text}</p>
              <p className={`text-[9px] mt-1 ${b.role === 'user' ? 'text-white/70' : 'text-muted-foreground'}`}>{fmtTs(b.ts)}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Summarize bar (acts like a textbox row) */}
      <div className="flex-shrink-0 border-t p-3 bg-white/80 dark:bg-[#1a1a1a]/80 backdrop-blur-md">
        <Button onClick={() => setPopup(true)} disabled={bubbles.length === 0} className="w-full h-11 rounded-2xl bg-[#0EA5E9] hover:bg-[#0284C7] text-white" data-testid="summarize-ai-chat">
          <Sparkles className="w-4 h-4 mr-1" /> Summarize this conversation
        </Button>
      </div>

      <SummarizePopup open={popup} onClose={() => setPopup(false)} api={api} payload={{ user_id: userId, kind: 'ai_chat', character }} title={`${charObj.name} chat`} />
    </div>
  );
}

// ===================== Generic thread list page (team chats / external texts) =====================
function ThreadListPage({ api, user, userId, kind, label, fetchUrl, listKey, threadIdField, threadTitleField, secondaryField, popupTitlePrefix, threadIdToPayload, searchPlaceholder, searchFields, renderTitle }) {
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(fetchUrl);
        if (alive) setThreads(res.data[listKey] || []);
      } catch (e) { /* silent */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [api, fetchUrl, listKey]);

  const openThread = (t) => {
    const id = t[threadIdField];
    navigate(`/admin/user-communications/${userId}/${kind}/${encodeURIComponent(id)}`, {
      state: { thread: t, popupTitle: `${popupTitlePrefix}: ${t[threadTitleField] || id}`, payload: threadIdToPayload(t) },
    });
  };

  // Client-side filter across the configured fields. Falls back to the
  // thread's title + last_message when no explicit `searchFields` is
  // supplied so every list still supports a sensible search.
  const needle = search.trim().toLowerCase();
  const filteredThreads = !needle
    ? threads
    : threads.filter((t) => {
        const fields = searchFields || [threadTitleField, secondaryField, 'all_text'];
        return fields.some((f) => {
          const v = t[f];
          return typeof v === 'string' && v.toLowerCase().includes(needle);
        });
      });

  return (
    <div className="fixed inset-x-0 overflow-y-auto bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 space-y-3" style={{ top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))', bottom: 'calc(var(--app-bottom-h, 4rem) + env(safe-area-inset-bottom, 0px))' }} data-testid={`user-comm-${kind}-list`}>
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-full bg-white dark:bg-[#1a1a1a] flex items-center justify-center shadow-sm"><ArrowLeft className="w-4 h-4" /></button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate">{user?.name || 'User'}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </div>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={searchPlaceholder || `Search ${label.toLowerCase()}…`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          style={{ fontSize: '16px' }}
          data-testid={`search-${kind}`}
        />
      </div>
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : filteredThreads.length === 0 ? (
        <p className="text-center py-10 text-muted-foreground text-xs">{needle ? 'No matches.' : 'No threads.'}</p>
      ) : (
        <div className="space-y-2">
          {filteredThreads.map((t, i) => (
            <button key={t[threadIdField] || i} onClick={() => openThread(t)} className="w-full text-left rounded-xl bg-white dark:bg-[#1a1a1a] shadow-sm p-3 hover:shadow-md break-words" data-testid={`thread-row-${i}`}>
              <p className="font-semibold text-sm break-words">{renderTitle ? renderTitle(t) : (t[threadTitleField] || '(untitled)')}</p>
              {secondaryField && t[secondaryField] && <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5 break-words">{t[secondaryField]}</p>}
              {t.last_message_at && <p className="text-[10px] text-muted-foreground mt-0.5">{fmtTs(t.last_message_at)}</p>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ===================== Single thread / call detail page =====================
function ThreadDetailPage({ api, user, userId, kind, threadId, label }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const url = kind === 'team_chats'
          ? `/admin/communications/${userId}/team-chats/${threadId}`
          : `/admin/communications/${userId}/external-texts/${encodeURIComponent(threadId)}`;
        const res = await api.get(url);
        if (alive) setMessages(res.data.messages || []);
      } catch (e) { toast.error('Could not load thread'); }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [api, userId, kind, threadId]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages.length]);

  const isTeam = kind === 'team_chats';
  const popupPayload = isTeam
    ? { user_id: userId, kind: 'team_chat', conversation_id: threadId }
    : { user_id: userId, kind: 'external_text', phone_number: threadId };

  return (
    <div className="fixed inset-x-0 flex flex-col bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] kb-aware-bottom" style={{ top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))' }} data-testid="user-comm-thread-detail">
      <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-white/80 dark:bg-[#1a1a1a]/80 backdrop-blur-md border-b">
        <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-full hover:bg-gray-100 dark:hover:bg-[#252525] flex items-center justify-center"><ArrowLeft className="w-4 h-4" /></button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate break-words">{label}</p>
          <p className="text-[11px] text-muted-foreground truncate">{user?.name || 'User'}</p>
        </div>
      </div>

      <div ref={scrollerRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : messages.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-12">No messages.</p>
        ) : messages.map((m, i) => {
          const isOwn = isTeam ? (m.sender_id === userId) : (m.direction === 'outbound');
          return (
            <div key={m.id || i} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words shadow-sm ${isOwn ? 'bg-blue-500 text-white' : 'bg-white dark:bg-[#1a1a1a] text-foreground border'}`}>
                <p className="break-words">{m.content || m.body}</p>
                <p className={`text-[9px] mt-1 ${isOwn ? 'text-white/70' : 'text-muted-foreground'}`}>
                  {fmtTs(m.created_at)}
                  {m.sender_name ? ` · ${m.sender_name}` : ''}
                  {m.direction ? ` · ${m.direction}` : ''}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex-shrink-0 border-t p-3 bg-white/80 dark:bg-[#1a1a1a]/80 backdrop-blur-md">
        <Button onClick={() => setPopup(true)} disabled={messages.length === 0} className="w-full h-11 rounded-2xl bg-[#0EA5E9] hover:bg-[#0284C7] text-white" data-testid="summarize-thread"><Sparkles className="w-4 h-4 mr-1" /> Summarize this thread</Button>
      </div>

      <SummarizePopup open={popup} onClose={() => setPopup(false)} api={api} payload={popupPayload} title={label} />
    </div>
  );
}

// ===================== Calls page (list + detail) =====================
// `channel` is one of:
//   - "team"     → internal WebRTC voice calls (Team Calls tab)
//   - "external" → Twilio PSTN calls          (External Calls tab)
//   - "video"    → internal video calls        (Video Calls tab)
// The list rows + detail view mirror the main /calls/history UI (direction
// icon, date/time, AI summary, Play Recording, Transcript) so the admin
// sees the SAME data they'd see on the user's own phone — minus the
// Call Back action (per spec).
const PhoneIncomingIcon = PhoneIncoming;
const PhoneOutgoingIcon = PhoneOutgoing;
const PhoneMissedIcon   = PhoneMissed;

const callDirection = (c) => {
  const raw = (c.call_type || c.direction || '').toLowerCase();
  if (c.was_missed || c.ended_with_voicemail) return 'missed';
  const status = (c.status || '').toLowerCase();
  if (status in { busy: 1, failed: 1, no_answer: 1, canceled: 1 }) return 'missed';
  if (raw.includes('miss'))    return 'missed';
  if (raw.includes('in'))      return 'inbound';
  if (raw.includes('out'))     return 'outbound';
  if (c.was_declined)          return 'declined';
  return raw || 'call';
};

const callDirectionIcon = (c) => {
  switch (callDirection(c)) {
    case 'inbound':  return <PhoneIncomingIcon className="w-4 h-4 text-green-500" />;
    case 'outbound': return <PhoneOutgoingIcon className="w-4 h-4 text-primary" />;
    case 'missed':   return <PhoneMissedIcon className="w-4 h-4 text-red-500" />;
    default:         return <Phone className="w-4 h-4 text-[#586D52]" />;
  }
};

const fmtDuration = (seconds) => {
  const s = Number(seconds) || 0;
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

function CallsListPage({ api, user, userId, channel }) {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const label = channel === 'external' ? 'External Calls'
    : channel === 'video' ? 'Video Calls'
    : 'Team Calls';
  const routeKey = channel === 'external' ? 'external_calls'
    : channel === 'video' ? 'video_calls'
    : 'team_calls';
  // Tailored search placeholders per tab (matches user spec in Msg 682).
  const searchPlaceholder = channel === 'external'
    ? 'Search by user, contact, phone or AI summary…'
    : channel === 'video'
    ? 'Search by user or AI summary…'
    : 'Search by user or AI summary…';

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/admin/communications/${userId}/calls`, { params: { channel, video: channel === 'video' } });
        if (alive) setCalls(res.data.calls || []);
      } catch (e) { /* silent */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [api, userId, channel]);

  // Safety net: ensure descending date order on the client even if the
  // backend mixes collections with different sort keys.
  const sortedCalls = [...calls].sort((a, b) => {
    const aKey = a.started_at || a.created_at || '';
    const bKey = b.started_at || b.created_at || '';
    return String(bKey).localeCompare(String(aKey));
  });

  // Tailored search fields per tab.
  //   - team   → user name + AI summary
  //   - video  → user name + AI summary
  //   - ext.   → user name + contact name + phone + AI summary
  const searchFields = channel === 'external'
    ? ['display_name', 'contact_name', 'contact_phone', 'external_phone_number', 'phone_number', 'from', 'callee_name', 'caller_name', 'summary', 'ai_summary', 'transcript']
    : ['display_name', 'callee_name', 'caller_name', 'summary', 'ai_summary', 'transcript'];

  const needle = search.trim().toLowerCase();
  const filteredCalls = !needle
    ? sortedCalls
    : sortedCalls.filter((c) => searchFields.some((f) => {
        const v = c[f];
        return typeof v === 'string' && v.toLowerCase().includes(needle);
      }));

  return (
    <div className="fixed inset-x-0 overflow-y-auto bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 space-y-3" style={{ top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))', bottom: 'calc(var(--app-bottom-h, 4rem) + env(safe-area-inset-bottom, 0px))' }} data-testid={`user-comm-${routeKey}-list`}>
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-full bg-white dark:bg-[#1a1a1a] flex items-center justify-center shadow-sm"><ArrowLeft className="w-4 h-4" /></button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate">{user?.name || 'User'}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </div>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          style={{ fontSize: '16px' }}
          data-testid={`search-${routeKey}`}
        />
      </div>
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : filteredCalls.length === 0 ? (
        <p className="text-center py-10 text-muted-foreground text-xs">{needle ? 'No matches.' : `No ${label.toLowerCase()}.`}</p>
      ) : (
        <div className="space-y-2">
          {filteredCalls.map((c, i) => {
            // Backend now enriches every row with a resolved `display_name`
            // — "Contact Name (+1555…)" for external calls, or the other
            // participant's full name for internal calls. Prefer it; fall
            // back to the raw phone for defensive compat.
            const who = c.display_name
              || c.external_phone_number
              || c.phone_number
              || c.from
              || c.callee_name
              || c.caller_name
              || 'Call';
            const durationSec = c.duration_seconds || c.recording_duration || c.duration || 0;
            const hasSummary = !!(c.summary || c.ai_summary);
            return (
              <button
                key={c.id || i}
                onClick={() => navigate(`/admin/user-communications/${userId}/${routeKey}/${encodeURIComponent(c.id || i)}`, { state: { call: c } })}
                className="block w-full text-left rounded-xl bg-white dark:bg-[#1a1a1a] shadow-sm p-3 hover:shadow-md break-words"
                data-testid={`call-row-${i}`}
              >
                <div className="flex items-center gap-2">
                  {callDirectionIcon(c)}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{who}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {fmtTs(c.started_at || c.created_at)} · {fmtDuration(durationSec)}
                    </p>
                  </div>
                  <span className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground">
                    {callDirection(c)}
                  </span>
                </div>
                {hasSummary && (
                  <p className="text-xs text-muted-foreground line-clamp-2 mt-1 leading-snug break-words overflow-hidden">
                    {c.summary || c.ai_summary}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CallDetailPage({ api, user, userId, channel, callId }) {
  const navigate = useNavigate();
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [popup, setPopup] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);

  const channelKind = channel === 'external' ? 'external_call'
    : channel === 'video' ? 'video_call'
    : 'team_call';
  const pageLabel = channel === 'external' ? 'External Call'
    : channel === 'video' ? 'Video Call'
    : 'Team Call';

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/admin/communications/${userId}/calls`, {
          params: { channel, video: channel === 'video' },
        });
        const found = (res.data.calls || []).find((c) => (c.id || '') === callId);
        if (alive) setCall(found || null);
      } catch (e) { /* silent */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [api, userId, channel, callId]);

  // Play the recording. Twilio-served recordings go through the
  // authenticated proxy; internal recordings are already signed URLs
  // so we open them directly. Matches the logic in HistoryPage.
  const handlePlayRecording = async () => {
    if (!call) return;
    const apiBase = process.env.REACT_APP_BACKEND_URL || '';
    let url = null;
    let isInternalDirect = false;
    if (channel === 'external' && call.recording_url) {
      url = `${apiBase}/api/twilio/recording/${call.id}/audio`;
    } else if (call.recording_url && call.recording_url !== '__INTERNAL__') {
      url = call.recording_url.startsWith('http')
        ? call.recording_url
        : `${apiBase}${call.recording_url}`;
      isInternalDirect = true;
    } else if (call.recording_available) {
      url = `${apiBase}/api/video-calls/${call.id}/recording`;
    }
    if (!url) {
      toast.message('Recording not available yet — usually appears within a minute of hangup.');
      return;
    }
    try {
      const headers = isInternalDirect
        ? {}
        : { Authorization: `Bearer ${localStorage.getItem('orb_token') || ''}` };
      const resp = await fetch(url, { headers });
      if (!resp.ok) {
        toast.error(`Recording failed to load (HTTP ${resp.status})`);
        return;
      }
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const audio = new Audio(blobUrl);
      audio.play().catch((e) => toast.error(`Could not play: ${e?.message || e}`));
      toast.success('Playing recording');
    } catch (e) {
      toast.error(e?.message || 'Could not load recording');
    }
  };

  const direction = call ? callDirection(call) : '';
  const hasRecording = !!(call?.recording_url || call?.recording_available);
  const hasTranscript = !!(call?.transcript || (Array.isArray(call?.diarized_segments) && call.diarized_segments.length));

  return (
    <div className="fixed inset-x-0 flex flex-col bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] kb-aware-bottom" style={{ top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))' }} data-testid="user-comm-call-detail">
      <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-white/80 dark:bg-[#1a1a1a]/80 backdrop-blur-md border-b">
        <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-full hover:bg-gray-100 dark:hover:bg-[#252525] flex items-center justify-center"><ArrowLeft className="w-4 h-4" /></button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate">{pageLabel}</p>
          <p className="text-[11px] text-muted-foreground truncate">{user?.name || 'User'} · {fmtTs(call?.started_at || call?.created_at)}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : !call ? (
          <p className="text-center py-10 text-muted-foreground text-xs">Call not found.</p>
        ) : (
          <>
            {/* Row 1 — direction + who + when + duration (history-style) */}
            <div className="rounded-xl bg-white dark:bg-[#1a1a1a] shadow-sm p-3">
              <div className="flex items-center gap-3">
                {callDirectionIcon(call)}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">
                    {call.display_name
                      || call.external_phone_number
                      || call.phone_number
                      || call.from
                      || call.callee_name
                      || call.caller_name
                      || 'Call'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {fmtTs(call.started_at || call.created_at)}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-semibold">{fmtDuration(call.duration_seconds || call.recording_duration || call.duration)}</p>
                  <span className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground">
                    {direction}
                  </span>
                </div>
              </div>
            </div>

            {/* Row 2 — AI summary */}
            <div className="rounded-xl bg-white dark:bg-[#1a1a1a] shadow-sm p-3">
              <p className="text-[10px] uppercase tracking-wider font-bold text-[#586D52] mb-1">AI Summary</p>
              {call.summary || call.ai_summary ? (
                <p className="text-sm whitespace-pre-wrap break-words">{call.summary || call.ai_summary}</p>
              ) : (
                <p className="text-xs italic text-muted-foreground">
                  Call summary will appear here once the recording finishes processing.
                </p>
              )}
            </div>

            {/* Row 3 — Play Recording + Transcript (no Call Back) */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={hasRecording ? handlePlayRecording : () => toast.message('No recording available for this call')}
                data-testid={`detail-play-recording-${call.id}`}
              >
                <Phone className="w-3.5 h-3.5 mr-1.5" />
                Play Recording
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={hasTranscript ? () => setShowTranscript(true) : () => toast.message('No transcript available for this call')}
                data-testid={`detail-view-transcript-${call.id}`}
              >
                <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                Transcript
              </Button>
            </div>
          </>
        )}
      </div>

      <div className="flex-shrink-0 border-t p-3 bg-white/80 dark:bg-[#1a1a1a]/80 backdrop-blur-md">
        <Button onClick={() => setPopup(true)} disabled={!call} className="w-full h-11 rounded-2xl bg-[#0EA5E9] hover:bg-[#0284C7] text-white" data-testid="summarize-call">
          <Sparkles className="w-4 h-4 mr-1" /> Summarize this call
        </Button>
      </div>

      {/* Transcript inline modal */}
      {showTranscript && call && (call?.diarized_segments || call?.transcript) && createPortal(
        <div className="fixed inset-0 z-[9999] bg-black/60 flex items-end justify-center" onClick={() => setShowTranscript(false)}>
          <div
            className="bg-white dark:bg-[#1a1a1a] rounded-t-3xl w-full max-w-xl shadow-2xl flex flex-col"
            style={{ height: '80vh', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <p className="font-bold text-sm">Call Transcript</p>
              <button onClick={() => setShowTranscript(false)} className="w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-[#252525] flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <DiarizedTranscript
                segments={call.diarized_segments}
                fallbackText={call.transcript}
              />
            </div>
          </div>
        </div>,
        document.body
      )}

      <SummarizePopup
        open={popup}
        onClose={() => setPopup(false)}
        api={api}
        payload={{ user_id: userId, kind: channelKind, call_id: callId }}
        title={`${pageLabel}: ${fmtTs(call?.started_at || call?.created_at)}`}
      />
    </div>
  );
}

import BusinessCardStatsPage from './BusinessCardStatsPage';
import UserActivityPage from './UserActivityPage';

// ===================== Single user → channel chooser =====================
function UserChannelChooser({ api, userId }) {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [popup, setPopup] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/admin/communications/users');
        const u = (res.data.users || []).find((x) => x.id === userId);
        if (alive) setInfo(u || null);
      } catch (e) { /* silent */ }
    })();
    return () => { alive = false; };
  }, [api, userId]);

  // Push the active user's name into the global page header so
  // it matches every other detail page in the app (back button +
  // title in the unified header bar instead of a duplicate inline
  // header below it).
  usePageHeader({
    title: info?.name || 'User',
    back: '/admin/user-communications',
  }, [info?.name]);

  return (
    <div
      className="fixed inset-x-0 flex flex-col bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] kb-aware-bottom"
      style={{ top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))' }}
      data-testid="user-comm-detail"
    >
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {info?.email && (
          <p className="text-xs text-muted-foreground truncate" data-testid="usercomm-detail-email">{info.email}</p>
        )}

        <div className="flex flex-col gap-2">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => navigate(`/admin/user-communications/${userId}/${t.id}`)}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white dark:bg-[#1a1a1a] shadow-sm hover:shadow-md transition-shadow"
                data-testid={`usercomm-tab-${t.id}`}
              >
                <Icon className="w-5 h-5 text-[#586D52]" />
                <span className="font-semibold text-sm">{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Sticky footer — matches the Summarize-this-chat button placement.
          Color (#7C3AED purple) matches the User Summarizer agent tile. */}
      <div className="flex-shrink-0 border-t p-3 bg-white/80 dark:bg-[#1a1a1a]/80 backdrop-blur-md">
        <Button
          onClick={() => setPopup(true)}
          className="w-full h-11 rounded-2xl bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
          data-testid="summarize-user-btn"
        >
          <Sparkles className="w-4 h-4 mr-1" />
          Summarize this User
        </Button>
      </div>

      <SummarizePopup
        open={popup}
        onClose={() => setPopup(false)}
        api={api}
        payload={{ user_id: userId, kind: 'user_summary' }}
        title={`${info?.name || 'User'} · Full Activity`}
      />
    </div>
  );
}

// ===================== User list =====================
function UserList({ api }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  usePageHeader({ title: 'User Communications' }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/communications/users');
        setUsers(res.data.users || []);
      } catch (e) { /* silent */ }
    })();
  }, [api]);

  const filteredUsers = users.filter((u) => !search.trim() ||
    (u.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-x-0 overflow-y-auto bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 space-y-3" style={{ top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))', bottom: 'calc(var(--app-bottom-h, 4rem) + env(safe-area-inset-bottom, 0px))' }} data-testid="user-comm-list">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Search users…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" style={{ fontSize: '16px' }} data-testid="usercomm-search" />
      </div>
      <div className="space-y-2">
        {filteredUsers.map((u) => (
          <button
            key={u.id}
            onClick={() => navigate(`/admin/user-communications/${u.id}`)}
            className="w-full flex items-center gap-3 p-3 rounded-xl bg-white dark:bg-[#1a1a1a] shadow-sm hover:shadow-md transition-shadow break-words"
            data-testid={`usercomm-user-${u.id}`}
          >
            <div className="w-10 h-10 rounded-full bg-[#586D52]/15 flex items-center justify-center text-[#586D52] font-bold flex-shrink-0">
              {(u.name || u.email || '?').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="font-semibold text-sm truncate">{u.name}</p>
              <p className="text-xs text-muted-foreground truncate">{u.email}</p>
            </div>
            <span className="text-[10px] uppercase font-bold tracking-wide text-muted-foreground">{u.role}</span>
          </button>
        ))}
        {filteredUsers.length === 0 && <p className="text-center text-muted-foreground py-6 text-xs">No users.</p>}
      </div>
    </div>
  );
}

// ===================== Main page (route-driven) =====================
export default function UserCommunicationsPage() {
  const { userId, channel, threadId } = useParams();
  const { api, user } = useAuth();
  const { isRestricted } = useStateScope();
  const [activeUser, setActiveUser] = useState(null);

  // Admins + restricted-state supervisors may both use this page.
  // For restricted supervisors, the backend _restricted_visibility_query
  // already scopes every users/contacts query to their state, so the
  // "history & assignments limited to that state's users" rule holds
  // without any extra client-side filtering.
  const isAdmin = user?.role === 'admin';
  // Special users with `admin_user_communications` toggle get the same
  // search + summarize access as full admins. Restricted-state
  // supervisors retain their existing scoped access.
  const canAccess = isAdmin
    || (user?.feature_toggles || {}).admin_user_communications === true
    || (isRestricted && user?.role === 'supervisor');

  // Fetch the active user info (for header) once
  useEffect(() => {
    if (!canAccess || !userId || !api) return;
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/admin/communications/users');
        const u = (res.data.users || []).find((x) => x.id === userId);
        if (alive) setActiveUser(u || null);
      } catch (e) { /* silent */ }
    })();
    return () => { alive = false; };
  }, [api, userId, canAccess]);

  if (!canAccess) return <div className="p-6 text-center text-muted-foreground">Admin access required.</div>;

  // Routing
  if (!userId) return <UserList api={api} />;
  if (!channel) return <UserChannelChooser api={api} userId={userId} />;

  if (channel === 'ai_chat') return <AiChatPage api={api} userId={userId} user={activeUser} />;
  if (channel === 'team_chats' && !threadId) return (
    <ThreadListPage
      api={api} user={activeUser} userId={userId} kind="team_chats" label="Team Chats"
      fetchUrl={`/admin/communications/${userId}/team-chats`} listKey="conversations"
      threadIdField="id" threadTitleField="other_user_name" secondaryField="last_message"
      popupTitlePrefix="Team Chat"
      threadIdToPayload={(t) => ({ user_id: userId, kind: 'team_chat', conversation_id: t.id })}
      searchPlaceholder="Search by user name or message…"
      searchFields={['other_user_name', 'last_message', 'all_text']}
    />
  );
  if (channel === 'team_chats' && threadId) return <ThreadDetailPage api={api} user={activeUser} userId={userId} kind="team_chats" threadId={threadId} label="Team Chat" />;
  if (channel === 'external_text' && !threadId) return (
    <ThreadListPage
      api={api} user={activeUser} userId={userId} kind="external_text" label="External Texts"
      fetchUrl={`/admin/communications/${userId}/external-texts`} listKey="threads"
      threadIdField="phone_number" threadTitleField="phone_number" secondaryField="last_message"
      popupTitlePrefix="SMS"
      threadIdToPayload={(t) => ({ user_id: userId, kind: 'external_text', phone_number: t.phone_number })}
      searchPlaceholder="Search by user, contact, phone or message…"
      searchFields={['phone_number', 'contact_name', 'last_message', 'all_text']}
      renderTitle={(t) => t.contact_name ? `${t.contact_name} (${t.phone_number})` : (t.phone_number || '(untitled)')}
    />
  );
  if (channel === 'external_text' && threadId) return <ThreadDetailPage api={api} user={activeUser} userId={userId} kind="external_text" threadId={threadId} label={`SMS: ${threadId}`} />;
  // Legacy `calls` URL redirects to the new "team_calls" key.
  if (channel === 'calls') return <Navigate to={`/admin/user-communications/${userId}/team_calls${threadId ? `/${threadId}` : ''}`} replace />;
  if (channel === 'team_calls' && !threadId)     return <CallsListPage  api={api} user={activeUser} userId={userId} channel="team" />;
  if (channel === 'team_calls' && threadId)      return <CallDetailPage api={api} user={activeUser} userId={userId} channel="team"     callId={threadId} />;
  if (channel === 'external_calls' && !threadId) return <CallsListPage  api={api} user={activeUser} userId={userId} channel="external" />;
  if (channel === 'external_calls' && threadId)  return <CallDetailPage api={api} user={activeUser} userId={userId} channel="external" callId={threadId} />;
  if (channel === 'video_calls' && !threadId)    return <CallsListPage  api={api} user={activeUser} userId={userId} channel="video" />;
  if (channel === 'video_calls' && threadId)     return <CallDetailPage api={api} user={activeUser} userId={userId} channel="video"    callId={threadId} />;
  if (channel === 'business_card_stats')         return <BusinessCardStatsPage api={api} userId={userId} user={activeUser} SummarizePopupComponent={SummarizePopup} />;
  if (channel === 'user_activity')               return <UserActivityPage api={api} userId={userId} user={activeUser} SummarizePopupComponent={SummarizePopup} />;

  return <UserChannelChooser api={api} userId={userId} />;
}
